/**
 * Monday Mirror Autopilot — button + dialog (Settings / Queue tabs).
 * Board-side automation: Application-stage prep→UW, Processing outward
 * (offer/decline/IR triggers) + inward AI routing, and daily offer/IR
 * chasing. Final chase escalations are always left for a human.
 */
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Bot, Play, Check, X, AlertTriangle, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { ScheduleEditor, ScheduleBadge } from "@/components/AutopilotSchedule";
import {
  mirrorAutopilotSettings, mirrorAutopilotSaveSettings, mirrorAutopilotStatus,
  mirrorAutopilotQueue, mirrorAutopilotApprove, mirrorAutopilotDismiss,
  mirrorAutopilotRun,
} from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const STAGE_BADGE = {
  applications: { label: "Application prep", cls: "bg-blue-100 text-blue-800" },
  processing:   { label: "Processing",       cls: "bg-violet-100 text-violet-800" },
  offer_chase:  { label: "Offer chase",      cls: "bg-amber-100 text-amber-800" },
  ir_chase:     { label: "IR chase",         cls: "bg-cyan-100 text-cyan-800" },
};

function StageBadge({ stage }) {
  const m = STAGE_BADGE[stage] || { label: stage, cls: "bg-slate-100 text-slate-700" };
  return <Badge variant="outline" className={`text-[10px] border-0 ${m.cls}`}>{m.label}</Badge>;
}

export function MirrorAutopilotButton({ onExecuted }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try { const s = await mirrorAutopilotStatus(); if (alive) setStatus(s); }
      catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const pending = (status?.proposed || 0) + (status?.flagged || 0);
  return (
    <>
      <Button size="sm" variant={status?.enabled ? "default" : "outline"}
        className="h-8 text-xs relative" onClick={() => setOpen(true)}
        data-testid="mirror-autopilot-btn">
        <Bot className="w-3.5 h-3.5 mr-1" />
        Autopilot
        {status?.enabled && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        {pending > 0 && (
          <span className="ml-1.5 bg-rose-500 text-white rounded-full px-1.5 text-[10px]" data-testid="mirror-autopilot-badge">{pending}</span>
        )}
      </Button>
      {open && <MirrorAutopilotDialog status={status} onClose={() => setOpen(false)} onExecuted={onExecuted}
        refreshStatus={() => mirrorAutopilotStatus().then(setStatus).catch(() => {})} />}
    </>
  );
}

function MirrorAutopilotDialog({ status, onClose, onExecuted, refreshStatus }) {
  const [tab, setTab] = useState("queue");
  const { currentRegion } = useRegion();
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="mirror-autopilot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bot className="w-4 h-4 text-violet-600" /> Monday Mirror Autopilot
            <Badge variant="outline" className="text-[10px]" data-testid="mirror-ap-region-badge">
              {currentRegion === "AUS" ? "AUS board" : "UK board"}
            </Badge>
            <ScheduleBadge schedule={status?.schedule} testid="mirror-ap-schedule-badge" />
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b">
          {[["queue", "Queue & flags"], ["settings", "Settings"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition ${tab === k ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`mirror-ap-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "queue"
          ? <QueueTab onExecuted={onExecuted} refreshStatus={refreshStatus} />
          : <SettingsTab refreshStatus={refreshStatus} />}
      </DialogContent>
    </Dialog>
  );
}

function QueueTab({ onExecuted, refreshStatus }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [running, setRunning] = useState(false);
  const [view, setView] = useState("proposed");

  const load = useCallback(async () => {
    try { setData(await mirrorAutopilotQueue(view)); }
    catch { toast.error("Failed to load autopilot queue"); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, queueId, okMsg) => {
    setBusyId(queueId);
    try {
      await fn(queueId, user?.name || "Ops");
      toast.success(okMsg);
      await load(); refreshStatus(); onExecuted?.();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Action failed"));
      await load();
    } finally { setBusyId(null); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await mirrorAutopilotRun();
      toast.success(`Scan complete — ${r.proposed ?? 0} new proposal(s)${r.executed ? `, ${r.executed} auto-executed` : ""}`);
      await load(); refreshStatus();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Run failed"));
    } finally { setRunning(false); }
  };

  const rows = data?.queue || [];
  const flags = data?.flags || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {["proposed", "executed", "dismissed", "failed"].map(s => (
            <button key={s} onClick={() => setView(s)}
              className={`px-2 py-0.5 rounded text-[11px] border ${view === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600"}`}
              data-testid={`mirror-ap-view-${s}`}>
              {s}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={runNow} disabled={running} data-testid="mirror-ap-run-btn">
          {running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
          Scan now
        </Button>
      </div>

      {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" /> : (
        <>
          <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="mirror-ap-queue-list">
            {rows.length === 0 && <div className="text-xs italic text-slate-400 text-center py-4">Nothing {view}</div>}
            {rows.map(q => (
              <div key={q.queue_id} className="border rounded p-2 flex items-start gap-2 bg-white" data-testid={`mirror-ap-row-${q.queue_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold truncate">{q.item_name}</span>
                    {q.sf_number && <span className="text-[10px] text-slate-400">{q.sf_number}</span>}
                    <StageBadge stage={q.stage} />
                    <Badge variant="outline" className={`text-[10px] ${q.confidence >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {q.confidence}%
                    </Badge>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">{q.why}</div>
                  {q.result && <div className="text-[10px] text-slate-400 mt-0.5">→ {typeof q.result === "string" ? q.result : q.result.done}</div>}
                </div>
                {q.status === "proposed" && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" className="h-7 px-2 text-[11px]" disabled={busyId === q.queue_id}
                      onClick={() => act(mirrorAutopilotApprove, q.queue_id, "Executed")}
                      data-testid={`mirror-ap-approve-${q.queue_id}`}>
                      {busyId === q.queue_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busyId === q.queue_id}
                      onClick={() => act(mirrorAutopilotDismiss, q.queue_id, "Dismissed")}
                      data-testid={`mirror-ap-dismiss-${q.queue_id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {flags.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-amber-800 flex items-center gap-1 mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Needs a human ({flags.length})
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="mirror-ap-flags-list">
                {flags.map(f => (
                  <div key={`${f.item_id}-${f.stage}`} className="border border-amber-200 bg-amber-50 rounded p-1.5 text-[11px]">
                    <span className="font-medium">{f.item_name}</span>
                    {f.sf_number && <span className="text-slate-400"> · {f.sf_number}</span>}
                    <ul className="list-disc ml-4 text-amber-800">
                      {(f.flags || []).map((fl, i) => <li key={i}>{fl}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SettingsTab({ refreshStatus }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const { currentRegion } = useRegion();

  useEffect(() => {
    mirrorAutopilotSettings().then(setS).catch(() => toast.error("Failed to load settings"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await mirrorAutopilotSaveSettings(s);
      setS(r); refreshStatus();
      toast.success("Autopilot settings saved");
    } catch (e) {
      toast.error(errorDetailMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" />;

  const toggleHandle = (h) => setS(p => ({
    ...p,
    processing_handle: p.processing_handle.includes(h)
      ? p.processing_handle.filter(x => x !== h)
      : [...p.processing_handle, h],
  }));

  const Row = ({ title, desc, children }) => (
    <div className="border rounded-lg p-3 space-y-2">
      <div>
        <div className="text-xs font-semibold text-slate-800">{title}</div>
        {desc && <div className="text-[11px] text-slate-500">{desc}</div>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border rounded-lg p-3 bg-violet-50/50 border-violet-200">
        <div>
          <div className="text-xs font-semibold flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-violet-600" /> Autopilot master switch</div>
          <div className="text-[11px] text-slate-500">Scans the board every 10 minutes when on</div>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => setS(p => ({ ...p, enabled: v }))} data-testid="mirror-ap-enabled" />
      </div>

      <div className="border rounded-lg p-3" data-testid="mirror-ap-schedule-row">
        <ScheduleEditor
          value={s}
          onChange={(patch) => setS(p => ({ ...p, ...patch }))}
          testidPrefix="mirror-ap-schedule"
          tzLabel={currentRegion === "AUS" ? "Sydney time (AEST)" : "UK time"}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-medium text-slate-600">Mode</label>
          <Select value={s.mode} onValueChange={(v) => setS(p => ({ ...p, mode: v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="mirror-ap-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual — queue for approval</SelectItem>
              <SelectItem value="auto">Auto — execute above confidence</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-600">Min confidence to auto-execute (%)</label>
          <Input type="number" min={50} max={100} value={s.min_confidence}
            onChange={(e) => setS(p => ({ ...p, min_confidence: Number(e.target.value) }))}
            className="h-8 text-xs w-24" data-testid="mirror-ap-confidence" />
        </div>
      </div>

      <Row title="Application stage → UW"
        desc="Match & link the broker, fill Comms %, run clean-case checks (duplicates, SF#, broker match). Clean cases advance to UW; anything else is flagged for a human.">
        <div className="flex items-center gap-2">
          <Switch checked={s.app_stage_enabled} onCheckedChange={(v) => setS(p => ({ ...p, app_stage_enabled: v }))} data-testid="mirror-ap-appstage" />
          <span className="text-[11px] text-slate-600">{s.app_stage_enabled ? "On" : "Off"}</span>
        </div>
      </Row>

      <Row title="Processing"
        desc="Reads the why (decline reason / prepared offer / IR note) and fires the same triggers your processors click. Inward routing uses AI to move replies to UW - IR / Final Checks / Offer Accepted — it's the intricate one, keep an eye on it.">
        <div className="flex items-center gap-2">
          <Switch checked={s.processing_enabled} onCheckedChange={(v) => setS(p => ({ ...p, processing_enabled: v }))} data-testid="mirror-ap-processing" />
          <span className="text-[11px] text-slate-600">{s.processing_enabled ? "On" : "Off"}</span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="text-[11px] font-medium text-slate-600">Auto-handle</label>
            <div className="flex gap-3 mt-1">
              {[["offer", "Offers"], ["decline", "Declines"], ["ir", "IR"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-1 text-[11px] cursor-pointer">
                  <input type="checkbox" checked={s.processing_handle.includes(k)} onChange={() => toggleHandle(k)}
                    disabled={!s.processing_enabled} data-testid={`mirror-ap-handle-${k}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Direction</label>
            <Select value={s.processing_direction} onValueChange={(v) => setS(p => ({ ...p, processing_direction: v }))}>
              <SelectTrigger className="h-8 text-xs w-44" data-testid="mirror-ap-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outward">Outward only (send out)</SelectItem>
                <SelectItem value="inward">Inward only (back to credit)</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Row>

      <div className="grid grid-cols-2 gap-3">
        <Row title="Conditional Offer chasing" desc="Daily chase up the ladder. 3rd chase (→ Offer Rejected) always needs a human.">
          <div className="flex items-center gap-3">
            <Switch checked={s.offer_chase_enabled} onCheckedChange={(v) => setS(p => ({ ...p, offer_chase_enabled: v }))} data-testid="mirror-ap-offerchase" />
            <span className="text-[11px]">chase after</span>
            <Input type="number" min={1} max={14} value={s.offer_chase_after_days}
              onChange={(e) => setS(p => ({ ...p, offer_chase_after_days: Number(e.target.value) }))}
              className="h-7 text-xs w-16" data-testid="mirror-ap-offerchase-days" />
            <span className="text-[11px]">day(s)</span>
          </div>
        </Row>
        <Row title="Info Requested chasing" desc="Same ladder for IR. 3rd chase (→ Declined) always needs a human.">
          <div className="flex items-center gap-3">
            <Switch checked={s.ir_chase_enabled} onCheckedChange={(v) => setS(p => ({ ...p, ir_chase_enabled: v }))} data-testid="mirror-ap-irchase" />
            <span className="text-[11px]">chase after</span>
            <Input type="number" min={1} max={14} value={s.ir_chase_after_days}
              onChange={(e) => setS(p => ({ ...p, ir_chase_after_days: Number(e.target.value) }))}
              className="h-7 text-xs w-16" data-testid="mirror-ap-irchase-days" />
            <span className="text-[11px]">day(s)</span>
          </div>
        </Row>
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="mirror-ap-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
}
